import { Button, Modal, PanoramaViewer, useToast } from '@citizen-walk/shared-ui';
import type { FieldDef, PostPhotoReplacement, PostVisibility } from '@citizen-walk/shared-types';
import { getDownloadURL, ref as storageRef } from 'firebase/storage';
import { useEffect, useMemo, useRef, useState } from 'react';
import { storage } from '../../lib/firebase';
import { LocationPicker, PhotoPostFieldsForm, type LatLng } from '../bulk-photo-import';
import { PostDetailFieldsView } from './PostDetailFieldsView';
import {
  MODERATION_AI_STATUS_LABEL,
  moderationAiKeyOf,
  type ModerationContentEdit,
  type ModerationPost,
} from './usePostsModeration';
import { resolveChipColor, useAiCategoryMastersBySettings } from './useAiCategoryMastersBySettings';
import { useAiOutputFormatBySettings } from './useAiOutputFormatBySettings';
import { ClassificationsTable } from './ClassificationsTable';
import { VerificationInlineSummary } from './verificationSummary';
import { useReplacePostPhoto } from './useReplacePostPhoto';
import { useRepairPostThumbnails } from './useRepairPostThumbnails';
import { PhotoPrivacySection } from './PhotoPrivacySection';

/**
 * 投稿モデレーション 詳細 modal (admin 第 11 弾 + Sprint BG-DataSettings 22 で AI 詳細復元)。
 *
 * 旧 `old/apps/admin/src/pages/PostsModerationPage.tsx` L548-899 の detail panel を
 * 当 modal に集約。以下を v2 で復元:
 *   - 写真 (Storage download URL) 1 枚表示
 *   - title / authorNickname / createdAt / comment / visibility
 *   - AI 解析結果 (status + error + ECW: emotions chips + categories chips + reasons + raw JSON)
 *   - 🤖 再解析 button (force=true)
 *   - admin 用 visibility 変更 + 削除 button
 *
 * Admin Wave AW5 (docs/108) で追加復元:
 *   - AI 生成タイトル「投稿概要」(extraFields.aiGeneratedTitle → professional_insight)
 *   - 防災 / ウォーカブル分類テーブル (outputFormat 駆動の動的列、未設定時は固定列 fallback)
 *   - 専門家の一言 / いいね・もったいない・総合スコアバッジ
 *   - 検証集計サマリ (VerificationInlineSummary)
 *   - 入力フィールド (extraFields) をラベル付き dl 表示 (生 JSON は debug 用 details に残置)
 *
 * 旧との差分:
 *   - chip color は M2 master 駆動 (旧 formatCategoryLabel 踏襲)
 */

const VISIBILITY_LABEL: Record<PostVisibility, string> = {
  public: '公開',
  hidden_others: '本人のみ',
  hidden_all: '全員非表示',
};

const AI_STATUS_LABEL = MODERATION_AI_STATUS_LABEL;

export interface PostsModerationDetailProps {
  districtId: string;
  post: ModerationPost;
  canModerate: boolean;
  /**
   * docs/28: この管理者がぼかし前の写真を見られるか (master、または地区設定で許可された地区管理者)。
   * 未指定は false (ぼかし前の表示・ぼかしを外す操作を出さない)。
   */
  canSeeOriginal?: boolean;
  busy: boolean;
  /** 当 post の再解析 callable in-flight 中 (button disable 用)。 */
  analyzing?: boolean;
  /** 地区 postFields (内容編集の入力項目フォーム駆動)。未指定なら入力項目編集は出さない。 */
  postFields?: FieldDef[];
  onClose: () => void;
  onModerate: (next: PostVisibility) => void;
  onDelete: () => void;
  /** 投稿内容 (タイトル / コメント / 入力項目 / 位置) を保存。未指定なら内容編集 UI を出さない。 */
  onSaveContent?: (content: ModerationContentEdit) => Promise<void>;
  /** 🤖 再解析 button が押されたとき (force=true で aiAnalyzePost callable)。 */
  onAnalyze?: () => void;
  /**
   * 投稿者からの再解析の依頼に付いた観点の表示名 (docs/13 §9-2、docs/29)。依頼が無ければ未指定。
   * 依頼の中身 (日時・理由) は `post.reanalysisRequest` から出す。
   */
  reanalysisRequestLabel?: string;
  /**
   * 依頼された観点が追加の観点のとき、その観点で再解析する (既定の観点の依頼は「🤖 再解析」で足りるので未指定)。
   */
  onAnalyzeRequested?: () => void;
  /** 写真差し替え完了時 (一覧 refresh は onSnapshot で自動だが、modal 側 photo 再読込のため通知)。 */
  onReplaced?: () => void;
}

export function PostsModerationDetail({
  districtId,
  post,
  canModerate,
  canSeeOriginal = false,
  busy,
  analyzing = false,
  postFields,
  onClose,
  onModerate,
  onDelete,
  onSaveContent,
  onAnalyze,
  reanalysisRequestLabel,
  onAnalyzeRequested,
  onReplaced,
}: PostsModerationDetailProps) {
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [photoError, setPhotoError] = useState<string | null>(null);
  // 差し替え成功で increment し、canonical path 不変でも写真を再取得する (差し替え後の即時反映)。
  const [reloadTick, setReloadTick] = useState(0);
  const [replaceFile, setReplaceFile] = useState<File | null>(null);
  const [replaceReason, setReplaceReason] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { busy: replaceBusy, replace } = useReplacePostPhoto();
  const { busy: repairBusy, repair } = useRepairPostThumbnails();
  const { toast } = useToast();

  // ---- 内容編集 (タイトル / コメント / 入力項目 / 位置) ----
  const [editing, setEditing] = useState(false);
  const [savingContent, setSavingContent] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editComment, setEditComment] = useState('');
  const [editExtra, setEditExtra] = useState<Record<string, unknown>>({});
  const [editLoc, setEditLoc] = useState<LatLng | null>(null);
  const canEditContent = canModerate && !!onSaveContent;

  // 必須入力項目は地区の入力項目設定 (postFields の mode='required') に基づく。required が 1 つも
  // 定義されていない地区では、空投稿を防ぐ保険として従来の「タイトルかコメント」最低条件を残す。
  const requiredKeys = useMemo(
    () => (postFields ?? []).filter((f) => f.mode === 'required').map((f) => f.key),
    [postFields],
  );

  /**
   * 必須 postField で未入力のものの表示名一覧を返す (空なら全て満たす)。
   * 組込キー (title/comment/detail/location) は編集 UI の値で判定し、それ以外は editExtra で判定する。
   * 写真は本フォームで編集不可 (差し替えは別経路) のため必須判定から除外する。
   */
  function findMissingRequiredLabels(): string[] {
    if (!postFields) return [];
    const missing: string[] = [];
    for (const f of postFields) {
      if (f.mode !== 'required') continue;
      if (f.key === 'photo' || f.type === 'photo') continue;
      let ok: boolean;
      if (f.key === 'title') {
        ok = editTitle.trim() !== '';
      } else if (f.key === 'comment' || f.key === 'detail') {
        ok = editComment.trim() !== '';
      } else if (f.key === 'location' || f.type === 'location') {
        ok = editLoc !== null;
      } else {
        const v = editExtra[f.key];
        if (v === undefined || v === null) ok = false;
        else if (typeof v === 'string') ok = v.trim() !== '';
        else if (Array.isArray(v)) ok = v.length > 0;
        else if (f.type === 'rating-scale' && typeof v === 'number')
          ok = Number.isFinite(v) && !(f.optionsMeta?.excludeZero === true && v === 0);
        else ok = true; // number / boolean / date 等は存在すれば満たす
      }
      if (!ok) missing.push(f.label || f.key);
    }
    return missing;
  }

  function startEdit() {
    setEditTitle(post.title ?? '');
    setEditComment(post.comment ?? '');
    setEditExtra({ ...post.extraFields });
    // 0,0 は「位置なし」とみなす (古い投稿) → 地図は未設定スタート。
    setEditLoc(
      post.latitude === 0 && post.longitude === 0
        ? null
        : { latitude: post.latitude, longitude: post.longitude },
    );
    setEditing(true);
  }

  async function handleSaveContent() {
    if (!onSaveContent || savingContent) return;
    if (requiredKeys.length === 0) {
      // required 未設定の地区: 最低限「タイトルかコメント」のどちらかは必須 (空投稿防止)。
      if (editTitle.trim() === '' && editComment.trim() === '') {
        toast('error', 'タイトルかコメントのどちらかを入力してください');
        return;
      }
    } else {
      const missing = findMissingRequiredLabels();
      if (missing.length > 0) {
        toast('error', `必須項目が未入力です: ${missing.join('、')}`);
        return;
      }
    }
    setSavingContent(true);
    try {
      await onSaveContent({
        title: editTitle,
        comment: editComment.trim() ? editComment : null,
        location: editLoc,
        extraFields: editExtra,
      });
      toast('success', '投稿内容を更新しました');
      setEditing(false);
    } catch (e) {
      toast('error', `更新に失敗しました: ${(e as Error).message ?? 'unknown'}`);
    } finally {
      setSavingContent(false);
    }
  }
  const resolver = useAiCategoryMastersBySettings(post.aiAnalysisSettingsId);
  const emotionMaster = resolver.byTemplateVar.get('emotionMaster');
  const categoryMaster = resolver.byTemplateVar.get('categoryMaster');
  // 分類テーブルの列名動的化用 outputFormat schema (settingsId → M1 → M3)。null なら固定列 fallback。
  const outputFormat = useAiOutputFormatBySettings(post.aiAnalysisSettingsId);
  const outputSchema = outputFormat?.schema ?? null;

  useEffect(() => {
    const path = post.photoPath;
    if (!path) {
      setPhotoUrl(null);
      return;
    }
    setPhotoUrl(null);
    setPhotoError(null);
    let cancelled = false;
    getDownloadURL(storageRef(storage, path))
      .then((url) => {
        if (!cancelled) setPhotoUrl(url);
      })
      .catch((e: Error) => {
        if (!cancelled) setPhotoError(e.message);
      });
    return () => {
      cancelled = true;
    };
    // docs/28: ぼかしの作り直しでは写真のパスは変わらず URL (トークン) だけが変わる。URL の変化でも
    // 取り直し、公開中の写真の表示をぼかし済みに更新する。
  }, [post.photoPath, post.photoUrl, reloadTick]);

  const aiKey = moderationAiKeyOf(post);
  const ecw = post.aiAnalysisEcw;
  const bousai = post.aiAnalysisBousai;
  const walker = post.aiAnalysisWalker;
  const bousaiClassifications = (bousai?.classifications ?? []) as Record<string, unknown>[];
  const walkerClassifications = (walker?.classifications ?? []) as Record<string, unknown>[];
  // AI 生成タイトル: extraFields.aiGeneratedTitle → bousai/walker professional_insight の順 (old2 L649-659)。
  const aiGeneratedTitle =
    post.aiGeneratedTitle ??
    (bousai?.professional_insight?.trim() || walker?.professional_insight?.trim() || null);

  const replacements = [...post.photoReplacements].sort((a, b) =>
    (b.replacedAt ?? '').localeCompare(a.replacedAt ?? ''),
  );

  async function handleRepairThumb() {
    if (repairBusy) return;
    try {
      const r = await repair(districtId, [post.id]);
      if (r.repaired > 0) {
        toast('success', 'サムネイルを再生成しました');
        setReloadTick((t) => t + 1);
        onReplaced?.();
      } else {
        const reason = r.results[0]?.reason ?? '対象外でした';
        toast('error', `再生成できませんでした: ${reason}`);
      }
    } catch (e) {
      toast('error', `再生成に失敗しました: ${(e as Error).message ?? 'unknown'}`);
    }
  }

  async function handleReplace() {
    if (!replaceFile || replaceBusy) return;
    try {
      await replace(districtId, post.id, replaceFile, replaceReason.trim() || undefined);
      toast('success', '写真を差し替えました (旧画像はアーカイブ保持)');
      setReplaceFile(null);
      setReplaceReason('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      // canonical path は不変だが内容 (token) が変わったので写真を再取得する。
      setReloadTick((t) => t + 1);
      onReplaced?.();
    } catch (e) {
      toast('error', `差し替え失敗: ${(e as Error).message ?? 'unknown'}`);
    }
  }

  return (
    <Modal open onClose={onClose} title="投稿詳細" size="lg" closeOnOverlayClick={!busy}>
      <div className="grid grid-cols-1 gap-3">
        <div className="flex items-center gap-2 flex-wrap text-xs text-slate-600">
          <span className="font-bold text-slate-900 text-sm">{post.title || '(無題)'}</span>
          <span className="text-slate-500">by {post.authorNickname}</span>
          <span className="text-slate-400">
            {post.createdAt ? post.createdAt.toLocaleString('ja-JP') : '日時不明'}
          </span>
          {canEditContent && !editing && (
            <Button
              size="sm"
              variant="secondary"
              className="ml-auto"
              onClick={startEdit}
              disabled={busy}
            >
              ✏️ 内容を編集
            </Button>
          )}
        </div>

        {/* 内容編集パネル (タイトル / コメント / 入力項目 / 位置)。admin 直接 write、写真・公開範囲は別経路。 */}
        {canEditContent && editing && (
          <section className="rounded border border-primary-200 bg-primary-50/40 p-3 grid grid-cols-1 gap-3">
            <h3 className="text-sm font-bold text-slate-900">投稿内容を編集</h3>
            <label className="block text-sm">
              <span className="text-slate-600">
                タイトル{' '}
                {requiredKeys.includes('title') && <span className="text-danger-600">*</span>}
              </span>
              <input
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                placeholder="タイトル"
                disabled={savingContent}
                className="mt-1 block w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
              />
            </label>
            <label className="block text-sm">
              <span className="text-slate-600">
                コメント{' '}
                {(requiredKeys.includes('comment') || requiredKeys.includes('detail')) && (
                  <span className="text-danger-600">*</span>
                )}
              </span>
              <textarea
                value={editComment}
                onChange={(e) => setEditComment(e.target.value)}
                placeholder="コメント"
                rows={3}
                disabled={savingContent}
                className="mt-1 block w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
              />
              <span className="mt-0.5 block text-[11px] text-slate-400">
                {requiredKeys.length > 0
                  ? '「*」の付いた必須項目（入力項目設定で「必須」にした項目）をすべて入力すると保存できます。'
                  : 'タイトルかコメントのどちらかが入力されていれば保存できます。'}
              </span>
            </label>

            {postFields && postFields.length > 0 && (
              <div>
                <span className="text-sm text-slate-600">入力項目</span>
                <div className="mt-1">
                  <PhotoPostFieldsForm
                    postFields={postFields}
                    values={editExtra}
                    onChange={(key, value) => setEditExtra((prev) => ({ ...prev, [key]: value }))}
                  />
                </div>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-slate-600">位置</span>
                <span className="text-[11px] text-slate-400">
                  {editLoc ? '地図をクリック / ドラッグで修正' : '未設定（地図をクリックして指定）'}
                </span>
              </div>
              <LocationPicker
                value={editLoc}
                defaultCenter={editLoc ?? undefined}
                onChange={(loc) => setEditLoc(loc)}
              />
            </div>

            <div className="flex flex-wrap justify-end gap-2">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setEditing(false)}
                disabled={savingContent}
              >
                キャンセル
              </Button>
              <Button
                size="sm"
                variant="primary"
                onClick={() => void handleSaveContent()}
                disabled={savingContent}
              >
                {savingContent ? '保存中…' : '内容を保存'}
              </Button>
            </div>
          </section>
        )}

        {post.photoPath && (
          <div className="grid grid-cols-1 place-items-center bg-slate-50 rounded border border-slate-200 min-h-[12rem] overflow-hidden">
            {photoUrl && post.photo?.projection === 'equirectangular' ? (
              // docs/27: 360度写真は見回し表示 (全画面ボタン内蔵)。
              <PanoramaViewer
                src={photoUrl}
                baseHeadingDeg={post.photo.poseHeadingDeg ?? 0}
                className="w-full h-80"
                alt={post.title || '投稿写真 (360度)'}
              />
            ) : photoUrl ? (
              <img
                src={photoUrl}
                alt={post.title || '投稿写真'}
                className="max-h-80 max-w-full object-contain"
              />
            ) : photoError ? (
              <p className="text-xs text-danger-700 p-4">写真読込エラー: {photoError}</p>
            ) : (
              <p className="text-xs text-slate-500 p-4">写真読込中…</p>
            )}
          </div>
        )}

        {/* docs/28 §10-1: 写真のぼかし (状態・ぼかし前の確認・範囲の編集・失敗への対応) */}
        <PhotoPrivacySection
          districtId={districtId}
          postId={post.id}
          photoPath={post.photoPath}
          privacy={post.photoPrivacy}
          isPanorama={post.photo?.projection === 'equirectangular'}
          publishedUrl={photoUrl}
          canModerate={canModerate}
          canSeeOriginal={canSeeOriginal}
        />

        {!editing && post.comment && (
          <div className="rounded border border-slate-200 p-3 text-sm whitespace-pre-wrap">
            {post.comment}
          </div>
        )}

        {/* 投稿者からの AI 再解析の依頼 (docs/13 §9-2)。再解析が成功すると Functions が依頼を消す。 */}
        {post.reanalysisRequest && (
          <section
            className="rounded border border-warning-200 bg-warning-50 p-3 grid grid-cols-1 gap-2"
            aria-labelledby="reanalysis-request-section"
            data-testid="reanalysis-request-section"
          >
            <h3 id="reanalysis-request-section" className="text-sm font-bold text-slate-900">
              🙋 投稿者から再解析の依頼があります
            </h3>
            <dl className="grid grid-cols-1 gap-1 text-xs text-slate-700">
              <div className="flex flex-wrap gap-x-2">
                <dt className="text-slate-500">依頼日時</dt>
                <dd>
                  {post.reanalysisRequest.requestedAt
                    ? post.reanalysisRequest.requestedAt.toLocaleString('ja-JP')
                    : '—'}
                </dd>
              </div>
              {reanalysisRequestLabel && (
                <div className="flex flex-wrap gap-x-2">
                  <dt className="text-slate-500">解析の観点</dt>
                  <dd>{reanalysisRequestLabel}</dd>
                </div>
              )}
              <div className="grid grid-cols-1 gap-0.5">
                <dt className="text-slate-500">理由</dt>
                <dd className="whitespace-pre-wrap break-words">
                  {post.reanalysisRequest.note ?? '（理由の入力はありません）'}
                </dd>
              </div>
            </dl>
            <p className="text-xs text-slate-500">
              {onAnalyzeRequested
                ? '依頼された観点で再解析すると、この依頼は消えます。'
                : '下の「🤖 再解析」で解析し直すと、この依頼は消えます。'}
            </p>
            {canModerate && onAnalyzeRequested && (
              <div className="flex justify-end">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={onAnalyzeRequested}
                  disabled={busy || analyzing}
                  data-testid="reanalysis-request-analyze"
                >
                  {analyzing ? '解析中…' : '🤖 依頼された観点で再解析'}
                </Button>
              </div>
            )}
          </section>
        )}

        {/* AI 解析結果 (旧 L548-899 踏襲、ECW chip + reasons + raw JSON) */}
        <section
          className="rounded border border-slate-200 p-3 grid grid-cols-1 gap-2"
          aria-labelledby="ai-analysis-section"
        >
          <div className="flex items-center justify-between gap-2">
            <h3 id="ai-analysis-section" className="text-sm font-bold text-slate-900">
              AI 解析結果
            </h3>
            <span className="text-xs text-slate-600">{AI_STATUS_LABEL[aiKey]}</span>
          </div>

          {/* 理由は Functions が日本語で入れる (error.message)。内部の分類コードは出さない。 */}
          {post.aiAnalysisError && (
            <p
              className={`text-xs whitespace-pre-wrap ${aiKey === 'failed' ? 'text-danger-700' : 'text-slate-700'}`}
            >
              {aiKey === 'failed' ? 'エラー: ' : '理由: '}
              {post.aiAnalysisError.message || '詳しい理由は記録されていません'}
            </p>
          )}

          {/* AI 生成タイトル「投稿概要」(old2 L648-671 踏襲) — 解析結果の最上部に常時表示 */}
          {aiGeneratedTitle && (
            <div className="px-3 py-2 rounded border border-primary-300 bg-white">
              <div className="text-[10px] uppercase tracking-wide text-primary-700 font-semibold mb-0.5">
                投稿概要
              </div>
              <p className="text-base font-medium text-slate-900 whitespace-pre-wrap break-words">
                {aiGeneratedTitle}
              </p>
            </div>
          )}

          {ecw && (
            <>
              {ecw.emotions.length > 0 && (
                <div>
                  <p className="text-[11px] text-slate-500 mb-1">感情</p>
                  <div className="flex flex-wrap gap-1">
                    {ecw.emotions.map((em) => {
                      const row = resolver.byCategoryLabel.get(em);
                      const color = row ? resolveChipColor(row, emotionMaster) : '#94A3B8';
                      return (
                        <span
                          key={`em-${em}`}
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium text-white"
                          style={{ backgroundColor: color }}
                        >
                          {em}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
              {ecw.categoryCodes.length > 0 && (
                <div>
                  <p className="text-[11px] text-slate-500 mb-1">カテゴリ</p>
                  <div className="flex flex-wrap gap-1">
                    {ecw.categoryCodes.map((code) => {
                      const row = resolver.byCategoryCd.get(code);
                      const color = row ? resolveChipColor(row, categoryMaster) : '#94A3B8';
                      return (
                        <span
                          key={`cat-${code}`}
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium text-white"
                          style={{ backgroundColor: color }}
                        >
                          {row?.category ?? code}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
              {ecw.description && (
                <div>
                  <p className="text-[11px] text-slate-500 mb-0.5">description</p>
                  <p className="text-xs text-slate-700 whitespace-pre-wrap">{ecw.description}</p>
                </div>
              )}
              {ecw.emotionReason && (
                <div>
                  <p className="text-[11px] text-slate-500 mb-0.5">感情の根拠</p>
                  <p className="text-xs text-slate-700 whitespace-pre-wrap">{ecw.emotionReason}</p>
                </div>
              )}
              {ecw.categoryReason && (
                <div>
                  <p className="text-[11px] text-slate-500 mb-0.5">カテゴリの根拠</p>
                  <p className="text-xs text-slate-700 whitespace-pre-wrap">{ecw.categoryReason}</p>
                </div>
              )}
            </>
          )}

          {/* ウォーカブル mode の結果 (old2 L716-769 踏襲) */}
          {walker && (
            <div className="grid grid-cols-1 gap-2 text-sm">
              {walker.description && (
                <DetailRow label="画像の説明">
                  <span className="whitespace-pre-wrap">{walker.description}</span>
                </DetailRow>
              )}
              {walker.professional_insight && (
                <DetailRow label="専門家の一言">
                  <span className="italic text-primary-900 bg-primary-50 border border-primary-200 rounded px-2 py-0.5">
                    {walker.professional_insight}
                  </span>
                </DetailRow>
              )}
              <WalkerScores walker={walker} />
              {walkerClassifications.length > 0 && (
                <ClassificationsTable
                  kind="walker"
                  schema={outputSchema}
                  classifications={walkerClassifications}
                />
              )}
            </div>
          )}

          {/* 防災 mode の結果 (old2 L772-797 踏襲) */}
          {bousai && (
            <div className="grid grid-cols-1 gap-2 text-sm">
              {bousai.description && (
                <DetailRow label="画像の説明">
                  <span className="whitespace-pre-wrap">{bousai.description}</span>
                </DetailRow>
              )}
              {bousai.professional_insight && (
                <DetailRow label="専門家の一言">
                  <span className="italic text-primary-900 bg-primary-50 border border-primary-200 rounded px-2 py-0.5">
                    {bousai.professional_insight}
                  </span>
                </DetailRow>
              )}
              {bousaiClassifications.length > 0 && (
                <ClassificationsTable
                  kind="bousai"
                  schema={outputSchema}
                  classifications={bousaiClassifications}
                />
              )}
            </div>
          )}

          {!ecw && !walker && !bousai && <p className="text-xs text-slate-500">AI 解析結果なし</p>}

          {/* AI 検証集計 (old2 L617-623 踏襲) */}
          {post.verificationSummary && (post.verificationSummary.completedResponses ?? 0) > 0 && (
            <div className="rounded border border-primary-200 bg-primary-50/40 p-2">
              <p className="text-xs font-semibold text-primary-800 mb-1">✓ AI 検証の集計</p>
              <VerificationInlineSummary summary={post.verificationSummary} />
            </div>
          )}

          {post.aiAnalysisRaw && (
            <details className="text-xs text-slate-600">
              <summary className="cursor-pointer">raw aiAnalysis JSON</summary>
              <pre className="mt-2 p-2 bg-slate-50 rounded overflow-auto text-[10px]">
                {JSON.stringify(post.aiAnalysisRaw, null, 2)}
              </pre>
            </details>
          )}

          {canModerate && onAnalyze && (
            <div className="flex justify-end">
              <Button
                size="sm"
                variant="secondary"
                onClick={onAnalyze}
                disabled={busy || analyzing}
              >
                {analyzing ? '解析中…' : '🤖 再解析'}
              </Button>
            </div>
          )}
        </section>

        <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
          <dt className="text-slate-500">visibility</dt>
          <dd className="text-slate-800">
            <code>{post.visibility}</code> ({VISIBILITY_LABEL[post.visibility]})
          </dd>
          <dt className="text-slate-500">authorUid</dt>
          <dd className="text-slate-800">
            <code className="text-[10px]">{post.authorUid}</code>
          </dd>
          <dt className="text-slate-500">postId</dt>
          <dd className="text-slate-800">
            <code className="text-[10px]">{post.id}</code>
          </dd>
          {post.aiAnalysisSettingsId && (
            <>
              <dt className="text-slate-500">aiAnalysisSettingsId</dt>
              <dd className="text-slate-800">
                <code className="text-[10px]">{post.aiAnalysisSettingsId}</code>
              </dd>
            </>
          )}
          {post.aiAnalysisAnalyzedAt && (
            <>
              <dt className="text-slate-500">analyzedAt</dt>
              <dd className="text-slate-800">
                <code className="text-[10px]">{post.aiAnalysisAnalyzedAt}</code>
              </dd>
            </>
          )}
        </dl>

        {/* 入力項目 — 地区の入力項目設定 (postFields) 駆動でラベル/型/順序を解決して表示。
            postFields 未渡し (古い呼出) のときは何も出さない (内部キー直書きの旧 fallback は廃止)。 */}
        {!editing && postFields && (
          <PostDetailFieldsView postFields={postFields} values={post.extraFields} />
        )}

        {/* 写真差し替え (master / district admin、旧画像はアーカイブ保持) */}
        {canModerate && (
          <section
            className="rounded border border-slate-200 p-3 grid grid-cols-1 gap-2"
            aria-labelledby="photo-replace-section"
          >
            <h3 id="photo-replace-section" className="text-sm font-bold text-slate-900">
              写真を差し替え
            </h3>
            {/* サムネイル再生成: 写真自体は変えず、未生成 / リンク失効で割れたサムネを作り直す。 */}
            {post.photoPath && (
              <div className="flex items-center justify-between gap-2 rounded bg-slate-50 border border-slate-200 px-2 py-1.5">
                <span className="text-[11px] text-slate-600">
                  サムネイルが表示されない / 古いままのときは再生成できます（写真は変わりません）。
                </span>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => void handleRepairThumb()}
                  disabled={repairBusy || busy}
                >
                  {repairBusy ? '再生成中…' : '🖼 サムネイルを再生成'}
                </Button>
              </div>
            )}
            <p className="text-[11px] text-slate-500">
              新しい写真に差し替えます。旧画像は削除せずアーカイブ保持され、下の履歴から確認できます。
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              aria-label="差し替え写真を選択"
              onChange={(e) => setReplaceFile(e.target.files?.[0] ?? null)}
              disabled={replaceBusy || busy}
              className="block w-full text-xs text-slate-700 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border file:border-slate-300 file:bg-slate-50 file:text-slate-700 file:text-xs file:cursor-pointer"
            />
            <input
              type="text"
              value={replaceReason}
              onChange={(e) => setReplaceReason(e.target.value)}
              placeholder="差し替え理由 (任意)"
              aria-label="差し替え理由"
              disabled={replaceBusy || busy}
              className="w-full text-sm px-3 py-2 border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-400"
            />
            <div className="flex justify-end">
              <Button
                size="sm"
                variant="primary"
                onClick={() => void handleReplace()}
                disabled={!replaceFile || replaceBusy || busy}
              >
                {replaceBusy ? '差し替え中…' : '写真を差し替え'}
              </Button>
            </div>
          </section>
        )}

        {/* 差し替え履歴 (新しい順) */}
        {replacements.length > 0 && (
          <section
            className="rounded border border-slate-200 p-3 grid grid-cols-1 gap-2"
            aria-labelledby="photo-replace-history-section"
          >
            <h3 id="photo-replace-history-section" className="text-sm font-bold text-slate-900">
              差し替え履歴 ({replacements.length})
            </h3>
            <ul className="grid grid-cols-1 gap-2">
              {replacements.map((r, i) => (
                <li
                  key={`${r.replacedAt}-${i}`}
                  className="flex items-start gap-3 bg-slate-50 rounded border border-slate-200 p-2"
                >
                  <ArchivedPhotoThumb
                    path={r.archivedThumbPath ?? r.archivedOriginalPath}
                    alt={`差し替え前の写真 (${r.replacedAt})`}
                  />
                  <div className="min-w-0 flex-1 text-xs text-slate-700">
                    <p className="text-slate-500 text-[11px]">
                      {r.replacedAt ? new Date(r.replacedAt).toLocaleString('ja-JP') : '日時不明'}
                    </p>
                    <p>差し替え者: {r.replacedByName ?? r.replacedByUid}</p>
                    {r.reason && (
                      <p className="text-slate-600 whitespace-pre-wrap">理由: {r.reason}</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {canModerate && (
          <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-200 mt-2">
            {(['public', 'hidden_others', 'hidden_all'] as const).map((v) => (
              <Button
                key={v}
                size="sm"
                variant={post.visibility === v ? 'primary' : 'secondary'}
                onClick={() => onModerate(v)}
                disabled={busy || post.visibility === v}
              >
                {VISIBILITY_LABEL[v]} へ
              </Button>
            ))}
            <Button size="sm" variant="danger" onClick={onDelete} disabled={busy}>
              完全削除
            </Button>
          </div>
        )}

        <div className="flex justify-end pt-2">
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            閉じる
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/** 詳細 modal の label + value 行 (old2 DL 相当)。 */
function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[7rem_1fr] gap-2">
      <dt className="text-slate-500 text-xs">{label}</dt>
      <dd className="text-slate-800 text-sm">{children}</dd>
    </div>
  );
}

/**
 * ウォーカブルの いいね！・もったいない・総合スコアバッジ (old2 L726-753 踏襲)。
 * いずれのスコアも無ければ何も描画しない。
 */
function WalkerScores({ walker }: { walker: NonNullable<ModerationPost['aiAnalysisWalker']> }) {
  const iine = walker.iine_score;
  const mot = walker.mottainai_score;
  const overall = walker.walkable_score;
  const hasIine = !!iine && (iine.sum !== 0 || iine.avg !== 0);
  const hasMot = !!mot && (mot.sum !== 0 || mot.avg !== 0);
  const hasOverall = overall != null && overall !== 0;
  if (!hasIine && !hasMot && !hasOverall) return null;
  return (
    <DetailRow label="いいね！・もったいない スコア">
      <div className="flex flex-wrap gap-2 text-xs">
        {iine && (
          <span className="px-2 py-0.5 bg-pink-100 text-pink-800 rounded">
            いいね！ sum={iine.sum ?? 0} avg={iine.avg ?? 0}
          </span>
        )}
        {mot && (
          <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded">
            もったいない sum={mot.sum ?? 0} avg={mot.avg ?? 0}
          </span>
        )}
        {overall != null && (
          <span className="px-2 py-0.5 bg-slate-100 text-slate-800 rounded">総合 {overall}</span>
        )}
      </div>
    </DetailRow>
  );
}

/** アーカイブ済 (差し替え前) 写真のサムネを Storage path から getDownloadURL で解決して表示。 */
function ArchivedPhotoThumb({ path, alt }: { path: string | null; alt: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setUrl(null);
    setError(false);
    // path が無い (旧画像が存在せずアーカイブしなかった) 場合はネットワークを叩かず空状態を表示。
    if (!path) return;
    getDownloadURL(storageRef(storage, path))
      .then((u) => {
        if (!cancelled) setUrl(u);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [path]);
  return (
    <div className="shrink-0 w-16 h-16 grid grid-cols-1 place-items-center bg-slate-100 rounded border border-slate-200 overflow-hidden">
      {!path ? (
        <span className="text-[10px] text-slate-400 px-1 text-center">アーカイブなし</span>
      ) : url ? (
        <img src={url} alt={alt} className="w-full h-full object-cover" />
      ) : error ? (
        <span className="text-[10px] text-danger-700 px-1 text-center">読込失敗</span>
      ) : (
        <span className="text-[10px] text-slate-400">…</span>
      )}
    </div>
  );
}
